import React, { useEffect, useState } from "react";
import { useQrCodeModal } from "./QrCodeModalContext";
import styles from "./styles/QrCodeModal.module.css";

const STATUS_TRANSLATIONS: Record<string, string> = {
  PENDENTE: "Pending",
  PAGO: "Paid",
  CANCELADO: "Canceled",
  EM_ANDAMENTO: "In Progress",
  CONCLUIDO: "Completed",
};

// 🔹 Utilitário para status e horários
const getTourStatus = (purchase: any) => {
  if (!purchase?.dataReserva || !purchase?.horaReserva) return {};

  const now = new Date();
  const tourStart = new Date(`${purchase.dataReserva}T${purchase.horaReserva}`);
  const marginMillis = 10 * 60 * 1000; // 10 minutos antes

  const canStart = now.getTime() >= tourStart.getTime() - marginMillis;
  const startAvailableAt = !canStart
    ? new Date(tourStart.getTime() - marginMillis).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const tourEnd = new Date(
    tourStart.getTime() +
      (purchase.pacote?.duracaoEmMinutos || 0) * 60 * 1000
  );
  const canComplete =
    now.getTime() >= tourEnd.getTime() &&
    purchase.status === "EM_ANDAMENTO";
  const completeAvailableAt = !canComplete
    ? tourEnd.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return { canStart, startAvailableAt, canComplete, completeAvailableAt };
};

// 🔹 Botão genérico
const TourButton = ({ label, onClick, disabled, loading }: any) => (
  <button className="btn btn-primary mt-4" onClick={onClick} disabled={disabled}>
    {loading ? `${label}...` : label}
  </button>
);

const QrCodeModal: React.FC = () => {
  const { isOpen, qrCodeData, closeModal } = useQrCodeModal();
  const [loading, setLoading] = useState(false);
  const [purchase, setPurchase] = useState<any>(null);
  const [driverLogged, setDriverLogged] = useState<any>(null);
  const [startingTour, setStartingTour] = useState(false);
  const [completingTour, setCompletingTour] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem("token");

  // 🔹 Fetch genérico com tratamento
  const fetchData = async (url: string, setter: Function) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Erro ${res.status} ao buscar ${url}`);

      // ⚙️ Alguns endpoints podem retornar 204 ou corpo vazio
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;

      setter(data);
    } catch (err: any) {
      console.error("Erro ao buscar dados:", err);
      setError("Erro ao carregar dados. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchData(`${process.env.REACT_APP_API_URL}/motorista/me`, setDriverLogged);
  }, [token]);

  useEffect(() => {
    if (isOpen && qrCodeData?.compraId) {
      fetchData(
        `${process.env.REACT_APP_API_URL}/pacotes/compra/${qrCodeData.compraId}`,
        (data: any) => {
          console.log("🔍 Dados da compra recebidos:", data);
          setPurchase(data);
        }
      );
    } else {
      setPurchase(null);
    }
  }, [isOpen, qrCodeData]);

  // 🔹 Ações: iniciar ou concluir tour
  const handleTourAction = async (action: "iniciar-tour" | "concluir-tour") => {
    if (!purchase) return;
    const setLoadingState =
      action === "iniciar-tour" ? setStartingTour : setCompletingTour;

    setError(null);
    setLoadingState(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/pacotes/${purchase.compraId}/${action}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // ⚙️ Mesmo tratamento de resposta
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;

      if (!res.ok) throw new Error(data?.message || "Erro ao processar ação");
      setPurchase(data);
    } catch (err: any) {
      console.error(`❌ Erro ao ${action}:`, err);
      setError(`Falha ao ${action === "iniciar-tour" ? "iniciar" : "concluir"} o tour.`);
    } finally {
      setLoadingState(false);
    }
  };

  if (!isOpen) return null;

  if (loading && !purchase)
    return <p className={styles.loading}>Loading...</p>;

  const isDriverOwner = driverLogged?.id === purchase?.motoristaId;
  const { canStart, startAvailableAt, canComplete, completeAvailableAt } =
    getTourStatus(purchase);

  return (
    <div className={styles.overlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h5 className={styles.title}>
            {purchase
              ? `Purchase #${purchase.compraId} - ${purchase.pacote?.titulo}`
              : "Your Purchase QR Code"}
          </h5>
          <button className={styles.btnClose} onClick={closeModal}>
            &times;
          </button>
        </div>

        <div className={styles.body}>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {purchase ? (
            <>
              <div className={styles.info}>
                <div className={styles.infoCard}>
                  <p>
                    <strong>Package:</strong> {purchase.pacote?.titulo}
                  </p>
                  <p>
                    <strong>Reservation Date:</strong>{" "}
                    {new Date(
                      `${purchase.dataReserva}T${purchase.horaReserva}`
                    ).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                  <p>
                    <strong>Total:</strong> € {purchase.precoTotal.toFixed(2)}
                  </p>
                </div>
                <div className={styles.infoCard}>
                  <p>
                    <strong>Driver:</strong>{" "}
                    {purchase.motoristaNome || purchase.motorista?.nome}
                  </p>
                  <p>
                    <strong>Passengers:</strong> {purchase.adultos} adults,{" "}
                    {purchase.criancas} children
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    <span
                      className={`${styles.status} ${purchase.status}`}
                    >
                      {STATUS_TRANSLATIONS[purchase.status] ||
                        purchase.status}
                    </span>
                  </p>
                </div>
              </div>

              {isDriverOwner && purchase.status !== "EM_ANDAMENTO" && (
                <>
                  <TourButton
                    label="Start Tour"
                    onClick={() => handleTourAction("iniciar-tour")}
                    disabled={!canStart}
                    loading={startingTour}
                  />
                  {!canStart && startAvailableAt && (
                    <p className="text-muted mt-1">
                      You can start the tour at{" "}
                      <strong>{startAvailableAt}</strong>.
                    </p>
                  )}
                </>
              )}

              {isDriverOwner && purchase.status === "EM_ANDAMENTO" && (
                <>
                  <TourButton
                    label="Conclude Tour"
                    onClick={() => handleTourAction("concluir-tour")}
                    disabled={!canComplete}
                    loading={completingTour}
                  />
                  {!canComplete && completeAvailableAt && (
                    <p className="text-muted mt-1">
                      You can complete the tour at{" "}
                      <strong>{completeAvailableAt}</strong>.
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            !loading && <p className={styles.noInfo}>No information available.</p>
          )}
        </div>

        <div className={styles.footer}>
          <button
            className={`btn btn-outline-danger ${styles.btnFooter}`}
            onClick={closeModal}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QrCodeModal;